import React from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Fonts, Radii, Shadows, StatusTints, Typography } from '../constants';

interface AlertDialogProps {
  visible: boolean;
  title: string;
  message: string;
  buttonText?: string;
  onDismiss: () => void;
  variant?: 'success' | 'error' | 'warning' | 'info';
  icon?: keyof typeof Ionicons.glyphMap;
}

export const AlertDialog: React.FC<AlertDialogProps> = ({
  visible,
  title,
  message,
  buttonText = 'OK',
  onDismiss,
  variant = 'info',
  icon,
}) => {
  const scaleValue = React.useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    if (visible) {
      Animated.spring(scaleValue, {
        toValue: 1,
        useNativeDriver: true,
        friction: 8,
        tension: 40,
      }).start();
    } else {
      scaleValue.setValue(0);
    }
  }, [visible, scaleValue]);

  // Fill = the button colour; the icon square uses the matching soft tint with
  // the tint's ink glyph (info reads as accent-strong, never the orange fill).
  const fill =
    variant === 'success' ? Colors.success
    : variant === 'error' ? Colors.error
    : variant === 'warning' ? Colors.warning
    : Colors.primary;
  const tint = StatusTints[variant === 'info' ? 'primary' : variant];

  const getIconName = (): keyof typeof Ionicons.glyphMap => {
    if (icon) return icon;
    switch (variant) {
      case 'success':
        return 'checkmark-circle';
      case 'error':
        return 'alert-circle';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'information-circle';
    }
  };

  // Use conditional rendering to ensure proper Modal cleanup on web
  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible={true}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <TouchableWithoutFeedback onPress={onDismiss}>
        <View style={styles.overlay}>
          <TouchableWithoutFeedback>
            <Animated.View
              style={[
                styles.modalContainer,
                {
                  transform: [{ scale: scaleValue }],
                },
              ]}
            >
              <View style={[styles.iconContainer, { backgroundColor: tint.bg }]}>
                <Ionicons name={getIconName()} size={40} color={tint.fg} />
              </View>

              <Text style={styles.title}>{title}</Text>
              <Text style={styles.message}>{message}</Text>

              <TouchableOpacity
                style={[styles.button, { backgroundColor: fill }]}
                onPress={onDismiss}
                activeOpacity={0.7}
              >
                <Text style={styles.buttonText}>{buttonText}</Text>
              </TouchableOpacity>
            </Animated.View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: Colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: Colors.white,
    borderRadius: Radii.dialog,
    padding: 28,
    width: '100%',
    maxWidth: 400,
    ...Shadows.panel,
  },
  iconContainer: {
    width: 72,
    height: 72,
    borderRadius: Radii.card,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    marginBottom: 20,
  },
  title: {
    ...Typography.section,
    color: Colors.textDark,
    textAlign: 'center',
    marginBottom: 12,
  },
  message: {
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.textMedium,
    textAlign: 'center',
    lineHeight: 23,
    marginBottom: 24,
  },
  button: {
    height: 48,
    paddingHorizontal: 20,
    borderRadius: Radii.control,
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonText: {
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    color: Colors.white,
  },
});
